<template>
  <avs-card class="p-2 mt-2">
    <!-- ===== HEADER / TOOLBAR ===== -->
    <template v-slot:title>
      <!-- Desktop: toolbar complète -->
      <div v-if="$vuetify.breakpoint.mdAndUp">
        <v-container fluid style="padding: 0;">
          <!-- Filtres -->
          <v-row>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="search"
                @input="getVehicleDebounced"
                clearable
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-magnify"
                :label="$t('commons.search')"
                class="search-field"
              />
            </v-col>

            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="selectedDepartment"
                clearable
                :items="departments"
                @input="resetAndLoad"
                item-text="name"
                item-value="id"
                :label="$t('Département')"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-office-building"
              />
            </v-col>

            <v-col cols="12" md="3">
              <v-autocomplete
                v-model="selectedProduct"
                clearable
                :items="products"
                @input="resetAndLoad"
                item-text="name"
                item-value="id"
                :label="$t('Produits')"
                outlined
                dense
                hide-details
                prepend-inner-icon="mdi-gas-station"
              />
            </v-col>

            <v-col cols="12" md="2">
              <v-switch
                v-model="showBlocked"
                :label="$t('Afficher bloqués')"
                class="mt-0"
                inset
                hide-details
                color="error"
                @change="resetAndLoad"
              />
            </v-col>
          </v-row>

          <!-- Actions / stats -->
          <v-row>
            <v-col cols="12" md="10">
              <v-row class="stats-section">
                <v-col cols="12">
                  <div class="stats-wrapper">
                    <div class="stats-container">
                      <!-- Carte Total -->
                      <v-card class="stat-card stat-card-main" elevation="4">
                        <div class="d-flex align-center justify-space-between">
                          <div>
                            <div class="stat-label">{{ $t('Total véhicules') }}</div>
                            <div class="stat-value">{{ totalVehicles }}</div>
                          </div>
                          <div class="stat-icon-wrapper">
                            <v-icon size="40" color="white">mdi-car-multiple</v-icon>
                          </div>
                        </div>
                      </v-card>

                      <!-- Carte Actifs -->
                      <v-card class="stat-card stat-card-success bgTable" elevation="3">
                        <div class="d-flex align-center justify-space-between">
                          <div>
                            <div class="stat-label-product">{{ $t('Véhicules actifs') }}</div>
                            <div class="stat-value-product">{{ activeVehicles }}</div>
                          </div>
                          <div class="stat-icon-wrapper-product stat-icon-success">
                            <v-icon size="32" color="white">mdi-check-circle</v-icon>
                          </div>
                        </div>
                      </v-card>

                      <!-- Carte Bloqués -->
                      <v-card class="stat-card stat-card-error bgTable" elevation="3">
                        <div class="d-flex align-center justify-space-between">
                          <div>
                            <div class="stat-label-product">{{ $t('Véhicules bloqués') }}</div>
                            <div class="stat-value-product">{{ blockedVehicles }}</div>
                          </div>
                          <div class="stat-icon-wrapper-product stat-icon-error">
                            <v-icon size="32" color="white">mdi-lock</v-icon>
                          </div>
                        </div>
                      </v-card>

                      <!-- Carte KM Total (si applicable) -->
                      <v-card v-if="canServe" class="stat-card stat-card-info bgTable" elevation="3">
                        <div class="d-flex align-center justify-space-between">
                          <div>
                            <div class="stat-label-product">{{ $t('Kilométrage total') }}</div>
                            <div class="stat-value-product">{{ formatNumber(totalKilometers) }}</div>
                          </div>
                          <div class="stat-icon-wrapper-product stat-icon-info">
                            <v-icon size="32" color="white">mdi-speedometer</v-icon>
                          </div>
                        </div>
                      </v-card>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-col>

            <v-col cols="12" md="2" class="d-flex flex-column align-end justify-center align-center">
              <slot name="actions" v-bind:dataset="dataset" />
              <v-btn
                color="success"
                outlined
                small
                @click="exportToExcel"
                class="export-btn mt-2"
                :loading="exporting"
                :disabled="exporting"
              >
                <v-icon small left>mdi-file-excel</v-icon>
                Excel
              </v-btn>
              <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    color="secondary"
                    outlined
                    small
                    class="mt-2"
                    v-bind="attrs"
                    v-on="on"
                    :loading="exportingBadges"
                    :disabled="exportingBadges"
                  >
                    <v-icon small left>mdi-download-multiple</v-icon>
                    {{ $t("Badges") }}
                  </v-btn>
                </template>

                <v-list dense>
                  <v-list-item @click="exportBadgesDepartment('v1')">
                    <v-list-item-icon><v-icon small>mdi-numeric-1-circle-outline</v-icon></v-list-item-icon>
                    <v-list-item-title>ZIP V1</v-list-item-title>
                  </v-list-item>

                  <v-list-item @click="exportBadgesDepartment('v2')">
                    <v-list-item-icon><v-icon small>mdi-numeric-2-circle-outline</v-icon></v-list-item-icon>
                    <v-list-item-title>ZIP V2</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>

            </v-col>
          </v-row>
        </v-container>
      </div>

      <!-- Mobile -->
      <div v-else class="col-12 mobile-header mt-5">
        <div class="d-flex align-center justify-space-between px-0 pt-4 pb-3">
          <slot name="actions-mobile" v-bind:dataset="dataset" />
        </div>

        <div class="mobile-filters px-0">
          <v-text-field
            v-model="search"
            @input="getVehicleDebounced"
            clearable
            dense
            outlined
            hide-details
            prepend-inner-icon="mdi-magnify"
            :label="$t('commons.search')"
            class="mb-3"
          />

          <v-autocomplete
            v-model="selectedDepartment"
            clearable
            :items="departments"
            @input="resetAndLoad"
            item-text="name"
            item-value="id"
            :label="$t('Département')"
            outlined
            dense
            hide-details
            prepend-inner-icon="mdi-office-building"
            class="mb-3"
          />

          <v-autocomplete
            v-model="selectedProduct"
            clearable
            :items="products"
            @input="resetAndLoad"
            item-text="name"
            item-value="id"
            :label="$t('Produits')"
            outlined
            dense
            hide-details
            prepend-inner-icon="mdi-gas-station"
            class="mb-3"
          />

          <div class="d-flex align-center mb-3">
            <v-switch
              v-model="showBlocked"
              :label="$t('Afficher bloqués')"
              class="mt-0 mr-3"
              inset
              hide-details
              color="error"
              @change="resetAndLoad"
            />
            <v-btn
              color="success"
              outlined
              x-small
              @click="exportToExcel"
              :loading="exporting"
              :disabled="exporting"
            >
              <v-icon small left>mdi-file-excel</v-icon>
              Excel
            </v-btn>
          </div>
        </div>
      </div>
    </template>

    <!-- ===== CONTENU ===== -->
    <template v-slot:content>
      <!-- Desktop : tableau -->
      <v-data-table
        v-if="$vuetify.breakpoint.mdAndUp"
        class="ma-6 bgTable"
        dense
        :headers="headersComputed"
        :no-data-text="$t('messages.information.no_data')"
        :search="search"
        :items="dataset"
        :loading-text="$t('Chargement des données')"
        :no-results-text="$tc('Aucune donnée disponible')"
        :loading="loading"
        :item-class="rowClass"
        disable-pagination
        hide-default-footer
        @dblclick:row="($event, { item }) => toEdit(item)"
      >
        <template v-slot:progress>
          <v-progress-linear indeterminate color="primary" class="mb-0" />
        </template>

        <!-- Conducteur -->
        <template #item.driver_last_name="{ item }">
          <div class="driver-info">
            <div class="driver-name">
              {{ (item.driver_last_name || 'N/A') + ' ' + (item.driver_first_name || '') }}
            </div>
            <div class="driver-phone grey--text text--darken-1">
              {{ item.driver_phone_number || '' }}
            </div>
          </div>
        </template>

        <template #item.monthly_limit="{ item }">
          {{ formatNumber(item.monthly_limit) }}
        </template>

        <!-- (dé)bloquer -->
        <template v-slot:item.is_blocked="{ item }">
          <template v-if="togglingId === item.id">
            <v-progress-circular indeterminate small class="mx-2" />
          </template>
          <template v-else>
            <avs-button
              icon
              :color="item.is_blocked ? 'error' : 'success'"
              :iconI="item.is_blocked ? 'mdi-lock-outline' : 'mdi-lock-open-outline'"
              fab
              small
              label
              :spantext="item.is_blocked ? $t('Débloquer le véhicule') : $t('Bloquer le véhicule')"
              @click.stop="confirmToggleBlock(item)"
              class="mx-2"
            />
          </template>
        </template>

        <template #item.kmh="{ item }">
          {{ formatNumber(item.kmh ?? 0) }}
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-end">
            <!-- validation dept -->
            <avs-button
              v-if="hasPendingDeptChange(item)"
              small
              text
              fab
              color="warning"
              class="blink-approval"
              label
              :iconI="'mdi-alert-decagram'"
              :spantext="$t('Valider le nouveau département')"
              @click.stop="openDeptApproval(item)"
            />

            <!-- KMH -->
            <avs-button
              v-if="isKmh(item)"
              small
              icon
              fab
              color="accent"
              label
              :iconI="'mdi-speedometer'"
              :spantext="$t('Historique kilométrage')"
              dark
              @click.stop="openKmhHistory(item)"
              :title="$t('Historique kilométrage')"
            />

            <!-- transactions -->
            <avs-button
              small
              fab
              icon
              color="primary"
              label
              :iconI="'mdi-finance'"
              :spantext="$t('Voir les transactions')"
              @click.stop="goToTransactions(item)"
            />

            <!-- éditer -->
            <avs-editButton
              v-if="!isDisabled(item)"
              @click.stop="toEdit(item)"
              :spantext="$t('Modifier le vehicule')"
            />
            <avs-editButton
              v-else
              :spantext="$t('Modifier')"
              @click.stop="openQuickEdit(item)"
            />

            <!-- badge V1/V2 -->
            <div class="d-flex justify-center">
              <template v-if="downloadingId === item.id">
                <v-progress-circular indeterminate small />
              </template>
              <template v-else>
                <v-menu offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <avs-button
                      icon
                      color="secondary"
                      :iconI="'mdi-download-outline'"
                      label
                      fab
                      small
                      :spantext="$t('Télécharger la carte')"
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>

                  <v-list dense>
                    <v-list-item @click.stop="downloadBadge(item, 'v1')">
                      <v-list-item-icon>
                        <v-icon small>mdi-numeric-1-circle-outline</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>V1</v-list-item-title>
                    </v-list-item>

                    <v-list-item @click.stop="downloadBadge(item, 'v2')">
                      <v-list-item-icon>
                        <v-icon small>mdi-numeric-2-circle-outline</v-icon>
                      </v-list-item-icon>
                      <v-list-item-title>V2</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </template>
            </div>

            <!-- supprimer -->
            <avs-deleteButton
              :disabled="isDisabled(item)"
              @click="handleOpenDeleteDialog(item)"
              :spantext="$t('actions.delete', { entity: $tc('vehicule').toLowerCase() })"
            />
          </div>
        </template>
      </v-data-table>

      <!-- Mobile : cartes -->
      <div v-else class="px-4 pt-2">
        <v-alert
          v-if="!dataset.length && !loading"
          type="info"
          dense
          outlined
          class="mb-2"
        >
          {{ $t('messages.information.no_data') }}
        </v-alert>

        <v-skeleton-loader
          v-if="loading"
          type="image, article, actions"
          class="mb-3"
        />

        <v-card
          v-for="it in dataset"
          :key="it.id"
          class="mb-3 vehicle-card"
          outlined
          @click="toEdit(it)"
        >
          <v-card-title class="py-3 d-flex align-center">
            <div class="vehicle-title">
              <div class="reg text-truncate">{{ it.registration_number || '—' }}</div>
              <div class="driver grey--text text--darken-1">
                {{ (it.driver_last_name || 'N/A') + ' ' + (it.driver_first_name || '') }}
              </div>
            </div>
            <v-spacer />
            <v-chip
              small
              label
              :color="it.is_blocked ? 'error' : 'success'"
              text-color="white"
              class="text-capitalize"
            >
              {{ it.is_blocked ? $t('Bloqué') : $t('Actif') }}
            </v-chip>
          </v-card-title>

          <v-divider />

          <v-card-text class="py-3">
            <div class="grid-2">
              <div class="cell">
                <div class="label">{{ $t('Departement') }}</div>
                <div class="value">{{ it.department_object?.name || '—' }}</div>
              </div>

              <div class="cell">
                <div class="label">{{ $t('Marque') }} / {{ $tc('Model') }}</div>
                <div class="value">{{ (it.make || '—') + ' / ' + (it.model || '—') }}</div>
              </div>

              <div class="cell">
                <div class="label">{{ $t('Année') }}</div>
                <div class="value">{{ it.year || '—' }}</div>
              </div>

              <div class="cell" v-if="canServe">
                <div class="label">{{ $t('Kilométrage') }}</div>
                <div class="value">{{ formatNumber(it.kmh ?? 0) }}</div>
              </div>
            </div>
          </v-card-text>

          <v-divider />

          <v-card-actions class="py-2 d-flex align-center">
            <v-btn
              v-if="hasPendingDeptChange(it)"
              fab
              small
              label
              color="warning"
              class="mx-1 blink-approval"
              @click.stop="openDeptApproval(it)"
              :title="$t('Valider le nouveau département')"
            >
              <v-icon left small>mdi-alert-decagram</v-icon>
            </v-btn>

            <template v-if="togglingId === it.id">
              <v-progress-circular indeterminate small class="mr-1" />
            </template>
            <template v-else>
              <v-btn
                icon
                fab
                small
                label
                :color="it.is_blocked ? 'success' : 'error'"
                @click.stop="confirmToggleBlock(it)"
                :title="it.is_blocked ? $t('Débloquer') : $t('Bloquer')"
                class="mx-1"
              >
                <v-icon small>
                  {{ it.is_blocked ? 'mdi-lock-open-outline' : 'mdi-lock-outline' }}
                </v-icon>
              </v-btn>
            </template>

            <v-spacer />

            <v-btn
              icon
              fab
              small
              label
              color="primary"
              @click.stop="goToTransactions(it)"
              :title="$t('Voir les transactions du véhicule')"
              class="mx-1"
            >
              <v-icon small>mdi-finance</v-icon>
            </v-btn>

            <v-btn
              icon
              fab
              small
              label
              color="primary"
              @click.stop="isDisabled(it) ? openQuickEdit(it) : toEdit(it)"
              :title="$t('actions.edit')"
              class="mx-1"
            >
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>

            <v-btn
              v-if="isKmh(it)"
              icon
              fab
              small
              label
              color="info"
              @click.stop="openKmhHistory(it)"
              :title="$t('Historique kilométrage')"
              class="mx-1"
            >
              <v-icon small>mdi-speedometer</v-icon>
            </v-btn>

            <v-menu offset-y>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  fab
                  small
                  label
                  color="secondary"
                  v-bind="attrs"
                  v-on="on"
                  :disabled="downloadingId === it.id"
                  :title="$t('Carte')"
                  class="mx-1"
                >
                  <v-icon small>mdi-download-outline</v-icon>
                </v-btn>
              </template>

              <v-list dense>
                <v-list-item @click.stop="downloadBadge(it, 'v1')">
                  <v-list-item-icon>
                    <v-icon small>mdi-numeric-1-circle-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>V1</v-list-item-title>
                </v-list-item>

                <v-list-item @click.stop="downloadBadge(it, 'v2')">
                  <v-list-item-icon>
                    <v-icon small>mdi-numeric-2-circle-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>V2</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <v-btn
              icon
              fab
              label
              color="error"
              @click.stop="handleOpenDeleteDialog(it)"
              :disabled="isDisabled(it)"
              :title="$t('actions.delete')"
              class="mx-1"
            >
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>

        <v-row justify="center" class="my-4" v-if="isFetchingMore && !isLastPage">
          <v-progress-circular indeterminate color="primary" />
        </v-row>
      </div>

      <!-- Delete dialog -->
      <avs-deleteDialog
        v-model="deleteDialogIsOpen"
        @cancel="handleCancel"
        @delete="handleDelete"
        resource="Vehicules"
      />

      <!-- Confirmation Bloquer/Débloquer -->
      <v-dialog v-model="blockDialogOpen" max-width="500" transition="dialog-bottom-transition">
        <v-card v-if="blockTarget" elevation="12" class="pa-4 rounded-lg">
          <v-card-title class="headline d-flex align-center">
            <v-icon left large :color="blockTarget.is_blocked ? 'warning' : 'error'" class="mr-2">
              {{ blockTarget.is_blocked ? 'mdi-lock-open' : 'mdi-lock' }}
            </v-icon>
            {{ blockTarget.is_blocked ? $t('Confirmation de déblocage') : $t('Confirmation de blocage') }}
          </v-card-title>

          <v-card-text class="subtitle-1 mb-4">
            {{ blockTarget.is_blocked
              ? $t('Confirmez-vous le déblocage du véhicule ') + `${blockTarget.registration_number}` + '?'
              : $t('Confirmez-vous le blocage du véhicule ') + `${blockTarget.registration_number}` + '?'
            }}
          </v-card-text>

          <v-divider class="mb-4" />

          <v-card-actions class="justify-end">
            <v-btn text @click="blockDialogOpen = false">{{ $t('Annuler') }}</v-btn>
            <v-btn dark color="black" @click="toggleBlockConfirmed">
              <v-icon left>{{ blockTarget.is_blocked ? 'mdi-lock-open-outline' : 'mdi-lock-outline' }}</v-icon>
              {{ $t('Confirmer') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Quick Edit -->
      <QuickVehicleEditDialog
        v-if="quickEdit.open && quickEdit.item"
        :key="quickEdit.item?.id || 'quick-edit'"
        v-model="quickEdit.open"
        :vehicle="quickEdit.item"
        @saved="onQuickSaved"
      />

      <!-- KMH History -->
      <QuickKmhHistoryDialog
        v-if="kmhDialog.open && kmhDialog.vehicle"
        :key="kmhDialog.vehicle?.id || 'kmh-history'"
        v-model="kmhDialog.open"
        :vehicle="kmhDialog.vehicle"
      />

      <!-- Validation changement de département -->
      <v-dialog v-model="deptApproval.open" max-width="600" transition="dialog-bottom-transition">
        <v-card v-if="deptApproval.request && deptApproval.vehicle">
          <v-card-title class="headline d-flex align-center">
            <v-icon left color="warning" class="mr-2">mdi-alert-decagram</v-icon>
            {{ $t('Validation changement de département') }}
          </v-card-title>

          <v-card-text>
            <p>
              {{ $t('Vous devez valider le changement de département pour le véhicule') }}
              <strong>{{ deptApproval.vehicle.registration_number }}</strong>.
            </p>

            <v-alert type="info" outlined dense class="mb-3">
              <div>
                {{ $t('Ancien département') }} :
                <strong>{{ deptApproval.request.old_department_name || '—' }}</strong>
              </div>
              <div>
                {{ $t('Nouveau département') }} :
                <strong>{{ deptApproval.request.new_department_name || '—' }}</strong>
              </div>
              <div v-if="deptApproval.request.has_vouchers" class="mt-2">
                <v-chip small color="warning" text-color="black" outlined>
                  <v-icon small left>mdi-gas-station</v-icon>
                  {{ $t('Ce véhicule possède déjà des bons en cours') }}
                </v-chip>
              </div>
            </v-alert>

            <v-textarea
              v-model="deptApproval.reason"
              :label="$t('Motif (optionnel)')"
              auto-grow
              rows="2"
              outlined
              dense
            />
          </v-card-text>

          <v-card-actions class="justify-end">
            <v-btn text @click="deptApproval.open = false">{{ $t('Annuler') }}</v-btn>
            <v-btn color="primary" dark :loading="deptApproval.loading" @click="approveDeptChange">
              <v-icon left small>mdi-check-circle</v-icon>
              {{ $t('Valider le changement') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </avs-card>
</template>

<script>
import { truncate, debounce } from "lodash";
import CompanyVehiclesService from "@/services/CompanyVehiclesService";
import { DepartmentService, ProductDetailService } from "@/services";
import { toDate } from "@/utils/datetime";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import QuickVehicleEditDialog from "@/components/ri3/companyVehicles/QuickVehicleEditDialog.vue";
import QuickKmhHistoryDialog from "@/components/ri3/companyVehicles/QuickKmhHistoryDialog.vue";

export default {
  name: "VehicleTable",
  components: { QuickVehicleEditDialog, QuickKmhHistoryDialog },
  props: {
    inspectionId: { type: Number, default: null },
  },
  data() {
    return {
      page: 1,
      exportingBadges: false,
      isFetchingMore: false,
      isLastPage: false,
      noNewItemsStreak: 0, // ✅ garde-fou scroll

      showBlocked: false,
      deleteDialogIsOpen: false,
      selectedItem: null,

      search: "",
      loading: false,
      dataset: [],

      downloadingId: null,
      togglingId: null,
      blockDialogOpen: false,
      blockTarget: null,

      getVehicleDebounced: null,
      quickEdit: { open: false, item: null },
      kmhDialog: { open: false, vehicle: null },

      exporting: false, // ✅ export

      // filtres
      departments: [],
      products: [],
      selectedDepartment: null,
      selectedProduct: null,

      // Totaux
      totalCount: 0,
      stats: { total: 0, active: 0, blocked: 0, total_kilometers: 0 },

      // Validation dept
      deptApproval: {
        open: false,
        vehicle: null,
        request: null,
        reason: "",
        loading: false,
      },

      headers: [
        { text: this.$t("Departement"), value: "department_object.name" },
        { text: this.$t("Marque"), value: "make", class: "min-width-150" },
        { text: this.$t("Année"), value: "year", width: "100" },
        { text: this.$tc("Model"), value: "model", align: "center" },
        { text: this.$t("Conducteur"), value: "driver_last_name", class: "min-width-150" },
        { text: this.$t("N° Matricule"), value: "registration_number", class: "min-width-150" },
        { text: this.$t(""), value: "is_blocked" },
        { text: this.$t("commons.actions"), value: "actions", align: "right", width: 100 },
      ],
    };
  },
  computed: {
    department() {
      return this.$store.state.app.project;
    },
    company() {
      return this.$store.getters["app/getCompany"] || this.$store.state.app.company || {};
    },
    canServe() {
      return this.company?.can_served === true || this.company?.can_serve === true;
    },
    headersComputed() {
      if (!this.canServe) return this.headers;
      const kmHeader = {
        text: this.$t("Kilométrage"),
        value: "kmh",
        align: "right",
        class: "min-width-120",
      };
      const base = [...this.headers];
      const actionsIdx = base.findIndex((h) => h.value === "actions");
      if (actionsIdx === -1) return base.concat(kmHeader);
      base.splice(actionsIdx, 0, kmHeader);
      return base;
    },
    totalVehicles() {
      if (typeof this.stats?.total === "number" && this.stats.total > 0) return this.stats.total;
      return this.totalCount || this.dataset.length;
    },
    activeVehicles() {
      return typeof this.stats?.active === "number"
        ? this.stats.active
        : this.dataset.filter((v) => !v.is_blocked).length;
    },
    blockedVehicles() {
      return typeof this.stats?.blocked === "number"
        ? this.stats.blocked
        : this.dataset.filter((v) => v.is_blocked).length;
    },
    totalKilometers() {
      return typeof this.stats?.total_kilometers === "number"
        ? this.stats.total_kilometers
        : this.dataset.reduce((sum, v) => sum + (Number(v.kmh) || 0), 0);
    },
  },
  methods: {
    truncate,
    toDate,
    async exportBadgesDepartment(version = "v1") {
      const deptId = this.selectedDepartment || this.department?.id;
      if (!deptId) {
        this.$store.commit("app/message", { msg: this.$t("Veuillez sélectionner un département."), color: "warning" });
        return;
      }

      this.exportingBadges = true;
      try {
        const blob = await CompanyVehiclesService.exportBadgesZip(this, {
          department: deptId,
          version,
          ...(this.showBlocked ? { is_blocked: true } : {}),
        });

        const deptName = this.departments.find(d => d.id === deptId)?.name || "departement";
        const dateStr = new Date().toISOString().slice(0, 10);

        saveAs(blob, `badges_${deptName}_${version}_${dateStr}.zip`);

        this.$store.commit("app/message", { msg: this.$t("Export badges terminé."), color: "success" });
      } catch (e) {
        this.$store.commit("app/message", { msg: this.$t("Erreur lors de l'export des badges."), color: "error" });
      } finally {
        this.exportingBadges = false;
      }
    },


    goToTransactions(item) {
      const plate = item && item.registration_number;
      if (!plate) {
        this.$store.commit("app/message", { msg: this.$t("Aucun matricule pour ce véhicule"), color: "warning" });
        return;
      }
      this.$router.push({ name: "TransactionDetail", query: { search: plate } });
    },

    resetAndLoad() {
      this.page = 1;
      this.dataset = [];
      this.isLastPage = false;
      this.noNewItemsStreak = 0;
      this.totalCount = 0;
      this.loading = true;
      this.stats = { total: 0, active: 0, blocked: 0, total_kilometers: 0 };
      this.loadMoreVehicles(true);
    },

    // ✅ Scroll robuste
    async loadMoreVehicles(isFirstPage = false) {
      if (this.isFetchingMore || this.isLastPage) return;
      this.isFetchingMore = true;

      const params = {
        search: this.search || undefined,
        department: this.selectedDepartment || this.department?.id || undefined,
        product: this.selectedProduct || undefined,
        page: this.page,
        is_blocked: this.showBlocked || undefined,
      };

      try {
        const response = await CompanyVehiclesService.get(this, null, params);

        // API non paginée
        if (Array.isArray(response)) {
          const seen = new Set(this.dataset.map((x) => x.id));
          const add = response.filter((x) => !seen.has(x.id));
          this.dataset.push(...add);
          this.totalCount = this.dataset.length;
          this.isLastPage = true;
          return;
        }

        const results = response?.results || [];

        // Totaux
        if (typeof response.total_count === "number") this.totalCount = response.total_count;
        else if (typeof response.count === "number") this.totalCount = response.count;

        if (response.stats) {
          this.stats = Object.assign({ total: 0, active: 0, blocked: 0, total_kilometers: 0 }, response.stats);
        }

        // Fin réelle
        if (!results.length) {
          this.isLastPage = true;
          return;
        }

        const existingIds = new Set(this.dataset.map((item) => item.id));
        const newItems = results.filter((item) => !existingIds.has(item.id));

        if (newItems.length) {
          this.dataset.push(...newItems);
          this.noNewItemsStreak = 0;
        } else {
          // ✅ si page = doublons (tri instable), on ne stoppe pas tout de suite
          this.noNewItemsStreak += 1;
        }

        // ✅ on avance la page même si 0 newItems (sinon “page 2 manquante”)
        this.page += 1;

        // Stop sur next/count si dispo
        if (response.next === null) this.isLastPage = true;
        if (typeof this.totalCount === "number" && this.totalCount > 0 && this.dataset.length >= this.totalCount) {
          this.isLastPage = true;
        }

        // ✅ garde-fou si backend renvoie les mêmes résultats en boucle
        if (this.noNewItemsStreak >= 3) {
          this.isLastPage = true;
          this.$store.commit("app/message", {
            msg: this.$t("Chargement stoppé (résultats identiques sur plusieurs pages). Vérifiez l'ordre/pagination côté API."),
            color: "warning",
          });
        }
      } catch (error) {
        if (error?.response?.status === 404) this.isLastPage = true;
        console.error("Erreur chargement vehicules:", error);
      } finally {
        this.isFetchingMore = false;
        if (isFirstPage) this.loading = false;
        this.$nextTick(() => { if (!this.isLastPage) this.handleScroll() });
      }
    },

    // ✅ Récupère toutes les pages pour l’export
    async fetchAllVehiclesForExport(baseParams) {
      const all = [];
      let page = 1;

      const MAX_PAGES = 10000;
      const seenPageFingerprints = new Set();

      for (let i = 0; i < MAX_PAGES; i++) {
        const res = await CompanyVehiclesService.get(this, null, { ...baseParams, page });

        if (Array.isArray(res)) return res;

        const results = res?.results || [];
        if (!results.length) break;

        // fingerprint ids pour éviter boucle si backend ignore page
        const ids = results.map((x) => x.id).join(",");
        if (seenPageFingerprints.has(ids)) break;
        seenPageFingerprints.add(ids);

        all.push(...results);

        if (res.next === null) break;

        const count = typeof res.total_count === "number" ? res.total_count : res.count;
        if (typeof count === "number" && all.length >= count) break;

        page += 1;
      }

      // dédup
      const byId = new Map(all.map((v) => [v.id, v]));
      return Array.from(byId.values());
    },

    // ✅ Export complet
    async exportToExcel() {
      if (this.exporting) return;
      this.exporting = true;

      try {
        const baseParams = {
          search: this.search || undefined,
          department: this.selectedDepartment || this.department?.id || undefined,
          product: this.selectedProduct || undefined,
          is_blocked: this.showBlocked || undefined,
          all: true, // optionnel, on ne s’y fie pas
        };

        const results = await this.fetchAllVehiclesForExport(baseParams);

        if (!results.length) {
          this.$store.commit("app/message", { msg: this.$t("messages.warning.no_data_to_export"), color: "warning" });
          return;
        }

        const exportData = results.map((item) => ({
          "N° Matricule": item.registration_number || "N/A",
          "Nom": item.driver_last_name || "N/A",
          "Prénom": item.driver_first_name || "N/A",
          "Téléphone": item.driver_phone_number || "N/A",
          "Département": item.department_object?.name || "N/A",
          "Marque": item.make || "N/A",
          "Modèle": item.model || "N/A",
          "Année": item.year || "N/A",
          "Statut": item.is_blocked ? "Bloqué" : "Actif",
          ...(this.canServe ? { "Kilométrage": Number(item.kmh ?? 0) } : {}),
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Véhicules");

        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        saveAs(blob, `vehicules_${new Date().toISOString().slice(0, 10)}.xlsx`);

        this.$store.commit("app/message", { msg: this.$t("Fichier Excel exporté avec succès"), color: "success" });
      } catch (e) {
        this.$store.commit("app/message", { msg: this.$t("Une erreur s'est produite lors de l'export."), color: "error" });
      } finally {
        this.exporting = false;
      }
    },

    handleScroll() {
      const scrollBottom = window.innerHeight + window.scrollY;
      const pageBottom = document.body.offsetHeight - 300;
      if (scrollBottom >= pageBottom) this.loadMoreVehicles();
    },

    confirmToggleBlock(item) {
      this.blockTarget = item;
      this.blockDialogOpen = true;
    },

    rowClass(item) {
      return item.is_blocked ? "blocked-row" : "";
    },

    toggleBlockConfirmed() {
      this.blockDialogOpen = false;
      if (this.blockTarget) {
        this.toggleBlock(this.blockTarget);
        this.blockTarget = null;
      }
    },

    toggleBlock(item) {
      this.togglingId = item.id;
      CompanyVehiclesService.toggleBlock(this, item.id)
        .then((response) => {
          const idx = this.dataset.findIndex((v) => v.id === item.id);
          if (idx !== -1) {
            this.$set(this.dataset, idx, { ...this.dataset[idx], is_blocked: response.is_blocked });
          }
          this.$store.commit("app/message", { msg: this.$t("messages.success.operation"), color: "success" });
        })
        .catch(() => {
          this.$store.commit("app/message", { msg: this.$t("Oops quelque chose a mal fonctionné"), color: "error" });
        })
        .finally(() => {
          this.togglingId = null;
        });
    },

    downloadBadge(item, version = "v1") {
      this.downloadingId = item.id;
      CompanyVehiclesService.getBadge(this, item.id, version)
        .then((blobUrl) => {
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = `badge_${item.registration_number}_${version}.png`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
        })
        .catch(() => {
          this.$store.commit("app/message", { msg: this.$t("messages.error.permission_denied"), color: "error" });
        })
        .finally(() => {
          this.downloadingId = null;
        });
    },

    getToEditLink(item) {
      return { name: "VehicleCreateUpdate", params: { id: item.id } };
    },

    isDisabled(item) {
      return Array.isArray(item.purchase_vouchers) && item.purchase_vouchers.length > 0;
    },

    isKmh(item) {
      return item.is_kmh;
    },

    formatNumber(value) {
      if (!value && value !== 0) return "";
      const n = Number(value) || 0;
      return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    },

    handleDelete() {
      CompanyVehiclesService.remove(this, this.selectedItem.id).then(() => {
        this.dataset = this.dataset.filter((e) => e.id !== this.selectedItem.id);
        this.$store.commit("app/message", {
          msg: this.$t("messages.success.deleted", { entity: this.$tc("Vehicule", 2) }),
          color: "success",
        });
      });
      this.deleteDialogIsOpen = false;
    },

    handleOpenDeleteDialog(item) {
      this.deleteDialogIsOpen = true;
      this.selectedItem = item;
    },

    handleCancel() {
      this.deleteDialogIsOpen = false;
    },

    toEdit(item) {
      this.$router.push(this.getToEditLink(item));
    },

    openQuickEdit(item) {
      this.quickEdit.item = item;
      this.quickEdit.open = true;
    },

    onQuickSaved(updated) {
      const i = this.dataset.findIndex((v) => v.id === updated.id);
      if (i !== -1) this.$set(this.dataset, i, { ...this.dataset[i], ...updated });
    },

    openKmhHistory(item) {
      this.kmhDialog.vehicle = item;
      this.kmhDialog.open = true;
    },

    fetchDepartments() {
      const companyPk = this.$store.state.app.company.pk;
      DepartmentService.getDepartment(companyPk).then((response) => {
        this.departments = response;
      });
    },

    fetchProducts() {
      ProductDetailService.getprincipalAll().then((response) => {
        this.products = response.results || [];
      });
    },

    // dept change
    hasPendingDeptChange(item) {
      return !!(item && item.pending_department_change && item.pending_department_change.status === "pending");
    },

    openDeptApproval(item) {
      if (!this.hasPendingDeptChange(item)) return;
      this.deptApproval.vehicle = item;
      this.deptApproval.request = item.pending_department_change;
      this.deptApproval.reason = "";
      this.deptApproval.open = true;
    },

    async approveDeptChange() {
      const req = this.deptApproval.request;
      const veh = this.deptApproval.vehicle;
      if (!req || !veh) return;

      this.deptApproval.loading = true;
      try {
        const resp = await CompanyVehiclesService.approveDepartmentChange(this, req.id, {
          reason: this.deptApproval.reason || null,
        });

        const updatedVehicle = resp.vehicle || null;
        if (updatedVehicle) {
          const idx = this.dataset.findIndex((v) => v.id === updatedVehicle.id);
          if (idx !== -1) this.$set(this.dataset, idx, updatedVehicle);
        }

        this.$store.commit("app/message", { msg: this.$t("Changement de département validé"), color: "success" });
        this.deptApproval.open = false;
      } finally {
        this.deptApproval.loading = false;
      }
    },
  },

  mounted() {
    this.getVehicleDebounced = debounce(() => {
      this.resetAndLoad();
    }, 500);

    this.fetchDepartments();
    this.fetchProducts();
    this.resetAndLoad();
    window.addEventListener("scroll", this.handleScroll);
  },

  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);
  },
};
</script>

<style scoped>
.search-field { border-radius: 12px; }

.export-btn {
  text-transform: none;
  font-weight: 600;
  border-radius: 8px;
}

/* ===== Section stats ===== */
.stats-section {
  margin-top: 10px;
  margin-bottom: 0px;
  padding: 5px 0;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
  border-radius: 16px;
}

.stats-wrapper { padding: 0 5px; }

.stats-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
}

/* Carte principale */
.stat-card-main {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%) !important;
  border-radius: 20px !important;
  padding: 24px;
  color: white;
  box-shadow: 0 10px 30px rgba(25, 118, 210, 0.3) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.stat-card-main:hover {
  transform: translateY(-6px);
  box-shadow: 0 15px 40px rgba(25, 118, 210, 0.4) !important;
}

.stat-icon-wrapper {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

.stat-label {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 700;
  margin-bottom: 8px;
}

.stat-value {
  font-size: 36px;
  font-weight: 800;
  line-height: 1;
  color: white;
}

/* Cartes secondaires */
.stat-card {
  border-radius: 16px !important;
  padding: 15px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid #e3f2fd;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08) !important;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12) !important;
}

.stat-card-success { border-color: #c8e6c9; }
.stat-card-error { border-color: #ffcdd2; }
.stat-card-info { border-color: #b3e5fc; }

.stat-icon-wrapper-product {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.stat-icon-success { background: linear-gradient(135deg, #4caf50 0%, #66bb6a 100%); }
.stat-icon-error { background: linear-gradient(135deg, #f44336 0%, #e57373 100%); }
.stat-icon-info { background: linear-gradient(135deg, #2196f3 0%, #64b5f6 100%); }

.stat-label-product {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 700;
  margin-bottom: 6px;
}

.stat-value-product {
  font-size: 24px;
  font-weight: 800;
  line-height: 1;
}

/* Table */
.driver-info { display: flex; flex-direction: column; line-height: 1.4; }
.driver-name { font-weight: 500; color: #1a202c; }
.driver-phone { font-size: 0.875rem; margin-top: 2px; }

.blocked-row { background-color: #f8d7da !important; }

/* Mobile */
.vehicle-card {
  border-radius: 12px;
  box-shadow: 0 6px 18px rgba(16, 24, 40, 0.06);
}

.vehicle-title { min-width: 0; }
.vehicle-title .reg { font-weight: 700; font-size: 16px; max-width: 60vw; }
.vehicle-title .driver { font-size: 12px; margin-top: 2px; }

.grid-2 { display: grid; grid-template-columns: 1fr 1fr; grid-gap: 8px 16px; }
.cell .label { font-size: 12px; color: #6b7280; }
.cell .value { font-size: 14px; font-weight: 600; }

/* Blink dept approval */
.blink-approval { animation: blink-approval 1.2s ease-in-out infinite; }
@keyframes blink-approval {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.4; transform: scale(1.05); }
}

@media (max-width: 360px) { .grid-2 { grid-template-columns: 1fr; } }
@media (max-width: 960px) { .stats-container { grid-template-columns: 1fr; } }
</style>
